import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { X, Minus, Plus, Trash2 } from "lucide-react";
import { useCart, MAX_PAIRS, BUNDLE_PRICE } from "@/context/CartContext";
import { money } from "@/lib/api";
import { WhatsAppIcon, waLink } from "@/components/WhatsApp";

const BundleBanner = () => {
  const { count, fullPrice, subtotal } = useCart();
  const left = MAX_PAIRS - count;
  return (
    <div data-testid="cart-bundle-banner" className="mx-6 mt-5 rounded-3xl bg-violet p-5">
      <p className="font-display text-xl font-extrabold uppercase leading-tight">Get 5 Insane Pairs for Just <span className="text-lime">999 Rs</span></p>
      <div className="mt-4 h-2 overflow-hidden rounded-full bg-ink/40">
        <motion.div className="h-full rounded-full bg-lime" animate={{ width: `${(count / MAX_PAIRS) * 100}%` }} transition={{ type: "spring", stiffness: 200, damping: 25 }} />
      </div>
      <p data-testid="cart-bundle-status" className="mt-3 font-mono text-xs uppercase tracking-wider text-white/90">
        {left > 0 ? `Add ${left} more pair${left > 1 ? "s" : ""} to unlock the 999 Rs deal` : `Deal unlocked! You save ${money(fullPrice - subtotal)}`}
      </p>
    </div>
  );
};

const buildMessage = (items, count, subtotal) => {
  const lines = items.map((i) => `- ${i.name} x ${i.quantity}`).join("\n");
  const deal = count === MAX_PAIRS ? ` (5 pairs deal for ${BUNDLE_PRICE} Rs)` : "";
  return `Hey Insane, Interested in these Pairs:\n${lines}\nTotal: ${count} pair${count > 1 ? "s" : ""}, ${money(subtotal)}${deal}\nPlease let's checkout`;
};

const CartLine = ({ item }) => {
  const { setQty, remove } = useCart();
  return (
    <motion.li layout initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 30 }}
      data-testid={`cart-item-${item.key}`} className="flex gap-4 border-b border-violet/25 py-5">
      <img src={item.image} alt={item.name} className="h-24 w-20 shrink-0 rounded-2xl object-cover" />
      <div className="flex flex-1 flex-col justify-between">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="font-display text-base font-bold uppercase leading-tight">{item.name}</p>
            <p className="mt-1 font-mono text-xs text-haze">{money(item.price)} each</p>
          </div>
          <button data-testid={`cart-remove-${item.key}`} onClick={() => remove(item.key)} className="text-haze transition-colors hover:text-pink" aria-label="Remove">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center rounded-full border border-violet/50">
            <button data-testid={`cart-qty-dec-${item.key}`} onClick={() => setQty(item.key, item.quantity - 1)} className="p-2 hover:text-lime"><Minus className="h-3.5 w-3.5" /></button>
            <span data-testid={`cart-qty-${item.key}`} className="w-6 text-center font-mono text-sm">{item.quantity}</span>
            <button data-testid={`cart-qty-inc-${item.key}`} onClick={() => { if (!setQty(item.key, item.quantity + 1)) toast.error("Max 5 pairs per order"); }} className="p-2 hover:text-lime"><Plus className="h-3.5 w-3.5" /></button>
          </div>
          <span className="font-mono text-sm font-bold">{money(item.price * item.quantity)}</span>
        </div>
      </div>
    </motion.li>
  );
};

export const CartDrawer = () => {
  const { items, open, setOpen, subtotal, fullPrice, count } = useCart();

  useEffect(() => {
    if (open) window.__lenis?.stop();
    else window.__lenis?.start();
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div key="overlay" data-testid="cart-overlay" onClick={() => setOpen(false)}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-ink/70 backdrop-blur-sm" />
          <motion.aside key="drawer" data-testid="cart-drawer"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="fixed right-0 top-0 z-[70] flex h-full w-full max-w-md flex-col bg-plum shadow-[-30px_0_80px_rgba(139,0,255,0.35)]">
            <div className="flex items-center justify-between border-b border-violet/30 px-6 py-5">
              <h2 className="font-display text-2xl font-extrabold uppercase">Your Bag <span className="text-lime">({count})</span></h2>
              <button data-testid="cart-close-btn" onClick={() => setOpen(false)} className="rounded-full p-2 transition-colors hover:bg-violet/40" aria-label="Close cart">
                <X className="h-5 w-5" />
              </button>
            </div>
            <BundleBanner />
            <div data-lenis-prevent className="flex-1 overflow-y-auto px-6">
              {items.length === 0 ? (
                <div data-testid="cart-empty" className="flex h-full flex-col items-center justify-center text-center">
                  <p className="font-display text-3xl font-extrabold uppercase">Bare feet?</p>
                  <p className="mt-2 text-sm text-haze">Your bag is empty. Go fix that.</p>
                </div>
              ) : (
                <ul><AnimatePresence initial={false}>{items.map((i) => <CartLine key={i.key} item={i} />)}</AnimatePresence></ul>
              )}
            </div>
            {items.length > 0 && (
              <div className="border-t border-violet/30 px-6 py-6">
                <div className="flex items-baseline justify-between">
                  <span className="font-mono text-xs uppercase tracking-widest text-haze">Subtotal</span>
                  <span className="flex items-baseline gap-3">
                    {fullPrice > subtotal && <span data-testid="cart-full-price" className="font-mono text-sm text-haze line-through">{money(fullPrice)}</span>}
                    <span data-testid="cart-subtotal" className="font-display text-3xl font-extrabold text-lime drop-shadow-[0_0_12px_rgba(204,255,0,0.6)]">{money(subtotal)}</span>
                  </span>
                </div>
                <motion.a data-testid="checkout-button" whileTap={{ scale: 0.97 }} href={waLink(buildMessage(items, count, subtotal))} target="_blank" rel="noopener noreferrer"
                  className="mt-5 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-[#25D366] px-6 py-4 font-display text-xs font-extrabold uppercase tracking-tight text-ink transition-colors hover:bg-lime sm:text-sm">
                  <WhatsAppIcon className="h-5 w-5 shrink-0" /> Checkout on WhatsApp
                </motion.a>
                <p className="mt-3 text-center font-mono text-[11px] uppercase tracking-wider text-haze">Your order is sent to us on WhatsApp to confirm</p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
